/*
 * SpecRef — render a spec reference with its resolvable tokens as deep-links.
 *
 * Drop-in for any place that previously rendered a raw spec-ref string
 * (e.g. `{specRef}` inside a styled <span>). Reference tokens become external
 * links to the exact source on GitHub (or rfc-editor.org); surrounding prose is
 * left as plain text. Links inherit the caller's colour so contextual accents
 * (indigo chips, teal strata) are preserved — see `.spec-link` in global.css.
 */

import { parseSpecRef } from "../spec/specRef";

interface SpecRefProps {
  /** The reference string, e.g. "Part IV §27 — RFC 9421 signed dispatch". */
  text: string;
}

export function SpecRef({ text }: SpecRefProps) {
  const segments = parseSpecRef(text);
  return (
    <>
      {segments.map((seg, i) =>
        seg.href ? (
          <a
            key={i}
            className="spec-link"
            href={seg.href}
            target="_blank"
            rel="noreferrer noopener"
            title={seg.title}
            // Some refs render inside a <summary> (collapsible strata); without
            // this, activating the link would also toggle the disclosure.
            onClick={(e) => e.stopPropagation()}
            // Move the new-tab + destination hint into the accessible name so it
            // reaches keyboard/screen-reader users, not just the title tooltip.
            aria-label={seg.title ? `${seg.title} (opens in a new tab)` : undefined}
          >
            {seg.text}
          </a>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}
